import React from "react";

import { Switch, Route, Redirect } from "react-router-dom";

import { PrivateRoute } from "./components/PrivateRoute";

import { SignIn } from "./pages/authentication/signIn";
import { ForgotPassword } from "./pages/authentication/forgotPassword";
import { ForgotPasswordSuccess } from "./pages/authentication/forgotPasswordSuccess";
import { ResetPassword } from "./pages/authentication/resetPassword";

import { BuilderHome } from "./pages/builder/home";

import { PresenterHome } from "./pages/presenter/PresenterHome";
import { ShareHome } from "./pages/share/ShareHome";
import { DownloadSlides } from "./pages/share/downloadSlides";

import { Settings } from "./pages/account/settings";
import { ConfirmationPage } from "./pages/payment/ConfirmationPage";

import { GoogleSlidesOAuthLandingPage } from "./pages/GoogleSlidesOAuthLandingPage";
import { useOfflineAssets } from "./pages/offline/useOfflineAssets";
import { useUser } from "./utilities/useUser";
import { useAppConfig } from "./utilities/useAppConfig";
import { ApplicationUser } from "./entities/ApplicationUser";

import { ProductsList } from "./pages/premiumSalesPages/ProductsList";
import { Product } from "./pages/premiumSalesPages/Product";
import { MarketingPageType } from "./pages/premiumSalesPages/types/MarketingPageType";
import { BuilderPreview } from "./pages/builderPreview/builderPreview";
import { LibraryPage } from "./pages/Library/LibraryPage";
import { routes } from "./routes";
import withRoot from "./withRoot";
import { LoadingPage } from "./components/LoadingPage";
import { NewRegister } from "./pages/authentication/NewRegistration";
import { CheckoutPage } from "./pages/payment/CheckoutPage";

function AppRoutes() {
	const userInfo = useUser();
	const { onlineUser } = userInfo;

	const offlineAssets = useOfflineAssets();
	const { onlineMode } = offlineAssets;

	if (!onlineMode) {
		return <OfflineRoutes />;
	}

	if (onlineUser === undefined) {
		return <LoadingPage />;
	}

	if (onlineUser && onlineUser.userMustSignUp && !onlineUser.makerSubscription) {
		return <ActivationRoute user={onlineUser} />;
	}

	return <OnlineRoutes user={onlineUser} />;
}

// Literally the only place we need withRoot
export default withRoot(AppRoutes);

function OfflineRoutes() {
	const userInfo = useUser();
	const { offlineUser } = userInfo;

	const [appConfig] = useAppConfig();

	if (!offlineUser) {
		return null;
	}

	return (
		<Switch>
			<PrivateRoute
				user={offlineUser}
				exact
				path={routes.app.builderPage}
				render={(routeProps) => (
					<BuilderHome
						{...routeProps}
						user={offlineUser}
						config={appConfig}
					/>
				)}
			/>
			<PrivateRoute user={offlineUser} exact path={routes.app.presenterPage} render={(routeProps) => <PresenterHome {...routeProps} />} />
			<PrivateRoute
				user={offlineUser}
				path={routes.app.libraryOfflinePage}
				render={() => <LibraryPage/> }
			/>
			<Redirect to={routes.app.libraryOfflinePage} />
		</Switch>
	);
}

function ActivationRoute(props: { user: ApplicationUser }) {
	const { user } = props;

	const userInfo = useUser();
	const { updateUser } = userInfo;

	const [appConfig] = useAppConfig();

	return (
		<Switch>
			<PrivateRoute 
				exact 
				path={routes.public.builderPreviewPage}
				user={user} 
				render={(routeProps) => (
					<BuilderPreview {...routeProps} appUser={user} config={appConfig}/>
					)}
			/>
			<PrivateRoute
				exact
				user={user}
				path={routes.public.resetPasswordPage}
				render={() => <ResetPassword />}
			/>
			<PrivateRoute 
				exact 
				user={user}
				path={routes.app.subscriptionConfirmationPage}
				render={() => <ConfirmationPage />}
			/>
			<PrivateRoute
				exact
				path={routes.app.checkoutPage}
				user={user}
				render={() => <CheckoutPage />}
			/> 
			<PrivateRoute
				exact
				path={routes.app.activatePage}
				user={user}
				render={() => <NewRegister />}
			/>
			<PrivateRoute 
				exact 
				path={routes.public.popularSeriesListPage} 
				user={user}
				render={() => <ProductsList pageType={MarketingPageType.Series} />} />
			<PrivateRoute
				exact
				path={routes.public.popularSeriesDetailPage}
				user={user}
				render={(routeProps) => <Product {...routeProps} pageType={MarketingPageType.Series} user={user} />}
			/>
			<PrivateRoute
				 exact
				 path={routes.public.popularSermonListPage} 
				 user={user}
				 render={() => <ProductsList pageType={MarketingPageType.Sermons}/>} />
			<PrivateRoute
				exact
				path={routes.public.popularSermonDetailPage}
				user={user}
				render={(routeProps) => <Product {...routeProps} pageType={MarketingPageType.Sermons} user={user} />}
			/>
			<Redirect to={routes.app.activatePage} />
		</Switch>
	);
}

interface OnlineRoutesProps {
	user: ApplicationUser | null;
}

function OnlineRoutes(props: OnlineRoutesProps) {
	const userInfo = useUser();
	const { updateUser } = userInfo;
	const onlineUser = props.user;

	const [appConfig] = useAppConfig();

	return (
		<Switch>
			<Route exact path={routes.public.signInPage} render={() => <SignIn config={appConfig} />} />
			<Route exact path={routes.public.reqisterPage} render={() => <NewRegister />} />
			<Route exact path={routes.public.forgotPasswordPage} render={() => <ForgotPassword />} />
			<Route exact path={routes.public.forgotPasswordSuccessPage} render={() => <ForgotPasswordSuccess />} />
			<Route exact path={routes.public.resetPasswordPage} render={() => <ResetPassword />} />
			<Route exact path={routes.public.popularSeriesListPage} render={() => <ProductsList pageType={MarketingPageType.Series} />} />
			<Route
				exact
				path={routes.public.popularSeriesDetailPage}
				render={(routeProps) => <Product {...routeProps} pageType={MarketingPageType.Series} user={onlineUser} />}
			/>
			<Route exact path={routes.public.popularSermonListPage} render={() => <ProductsList pageType={MarketingPageType.Sermons}/>} />
			<Route
				exact
				path={routes.public.popularSermonDetailPage}
				render={(routeProps) => <Product {...routeProps} pageType={MarketingPageType.Sermons} user={onlineUser} />}
			/>
			<Route exact path={routes.public.sharePage} render={() => <ShareHome />} />
			<Route exact path={routes.public.shareSlidePage} render={() => <DownloadSlides />} />
			<Route
				exact
				path={routes.public.googleAuthPage}
				render={(routeProps) => <GoogleSlidesOAuthLandingPage {...routeProps} config={appConfig} />}
			/>
			<Route 
				exact 
				path={routes.public.builderPreviewPage}
				render={(routeProps) => (
					<BuilderPreview {...routeProps} appUser={onlineUser} config={appConfig}/>
				)}
			/>
			<Route
				exact
				path={routes.app.subscriptionConfirmationPage}
				render={() => <ConfirmationPage />}
			/>
			<PrivateRoute exact path={routes.app.checkoutPage}	user={onlineUser} render={() => <CheckoutPage />}/>
			<PrivateRoute path={routes.app.activatePage} user={onlineUser} render={() => <NewRegister />}/>
			<PrivateRoute
				exact
				path={routes.app.builderPage}
				user={onlineUser}
				render={(routeProps) => (
					<BuilderHome
						{...routeProps}
						user={onlineUser!}
						config={appConfig}
					/>
				)}
			/>
			<PrivateRoute exact path={routes.app.presenterPage} user={onlineUser} render={(routeProps) => <PresenterHome {...routeProps} />} />
			<PrivateRoute
				exact
				path={routes.app.settings}
				user={onlineUser}
				render={() => <Settings />}
			/>
			<PrivateRoute
				path={[
					routes.app.libraryCalendarPage,
					routes.app.libraryHomePage, 
					routes.app.libraryFolder,
					routes.app.librarySearchPage, 
					routes.app.libraryStarredPage, 
					routes.app.libraryOfflinePage,
					routes.app.premiumSeriesListPage,
					routes.app.premiumSeriesDetailPage,
					routes.app.sermonTemplateListPage,
					routes.app.libraryRecentPage,
					routes.app.sermonTemplateDetailPage,
					routes.app.premiumSermonListPage,
					routes.app.premiumSermonDetailPage,
				]}
				exact
				user={onlineUser}
				render={() => <LibraryPage/> }
			/>
			<Redirect to={routes.app.libraryHomePage} />
		</Switch>
	);
}
